import { useEffect, useState } from 'react';

/**
 * Empreinte d'appareil MaxMind minFraud — **facultative**.
 *
 * L'anti-fraude est appliqué **par la plateforme**, au moment du
 * `POST /payment` et avant toute création chez Stripe : vous n'avez aucun
 * appel supplémentaire à écrire, et l'IP comme le user-agent du visiteur sont
 * relevés côté serveur.
 *
 * Ce hook ne sert qu'à l'option d'affinage : si votre formulaire embarque le
 * script de collecte d'empreinte de MaxMind, son identifiant de session peut
 * être joint au démarrage du paiement (`device_session_id`) pour améliorer le
 * scoring. Sans identifiant de compte MaxMind, le hook ne fait rien du tout et
 * le paiement fonctionne exactement de la même façon.
 *
 * Activez-le en renseignant `VITE_MAXMIND_ACCOUNT_ID` dans votre `.env`.
 */
export function useDeviceSessionId(): string | null {
    const [sessionId, setSessionId] = useState<string | null>(null);

    useEffect(() => {
        const accountId = (import.meta.env.VITE_MAXMIND_ACCOUNT_ID ?? '').trim();

        if (!accountId) {
            return;
        }

        let cancelled = false;

        // Import dynamique : le script n'est chargé que sur les pages qui en
        // ont besoin, et une panne de son côté ne bloque jamais le paiement.
        // L'URL passe par une variable pour que TypeScript ne cherche pas à
        // résoudre un module distant à la compilation.
        const moduleUrl = 'https://device.maxmind.com/js/device-module.js';

        import(/* @vite-ignore */ moduleUrl)
            .then((module: { trackDevice: (options: { accountId: string }) => Promise<{ trackingToken: string }> }) =>
                module.trackDevice({ accountId }),
            )
            .then(({ trackingToken }) => {
                if (!cancelled) {
                    setSessionId(trackingToken);
                }
            })
            .catch(() => {
                // Script bloqué (extension anti-pistage, réseau) : on continue
                // sans empreinte. Le paiement reste possible.
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return sessionId;
}
